import React from 'react';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';

interface Crystal {
  id: string;
  name: string;
  image: string;
  chakras: string[];
  vibrationFrequency?: string;
  categoryTaglines?: { [key: string]: string };
}

interface CrystalCardProps {
  crystal: Crystal;
  onClick: () => void;
  variant?: 'card' | 'grid' | 'mini' | 'detail' | 'inline';
  reason?: string;
  tagline?: string;
  showChakras?: boolean;
  showVibration?: boolean;
}

export function CrystalCard({ 
  crystal, 
  onClick, 
  variant = 'grid',
  reason,
  tagline,
  showChakras = true,
  showVibration = false
}: CrystalCardProps) {
  // Full card variant (used in Crystals tab)
  if (variant === 'card') {
    return (
      <Card 
        className="cursor-pointer hover:shadow-lg transition-all hover:scale-[1.02] bg-muted/30 border-0 shadow-sm"
        onClick={onClick}
      >
        <CardContent className="p-4">
          <div className="aspect-square mb-3 rounded-xl overflow-hidden bg-background/80 flex items-center justify-center p-3">
            <img 
              src={crystal.image} 
              alt={crystal.name}
              className="w-28 h-28 object-contain drop-shadow-md"
            />
          </div>
          <h3 className="font-medium text-primary text-center text-sm">{crystal.name}</h3>
          {showChakras && (
            <div className="flex flex-wrap gap-1 mt-2 justify-center">
              {crystal.chakras.slice(0, 2).map(chakra => (
                <Badge key={chakra} variant="secondary" className="text-xs bg-accent/20 text-primary border-0">
                  {chakra}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Grid variant (used in Remedies tab categories)
  if (variant === 'grid') {
    return (
      <div 
        className="cursor-pointer group"
        onClick={onClick}
      >
        <div className="aspect-square rounded-xl overflow-hidden bg-background/80 mb-2 group-hover:scale-105 transition-transform shadow-sm flex items-center justify-center p-2">
          <img 
            src={crystal.image} 
            alt={crystal.name}
            className="w-24 h-24 object-contain drop-shadow-md"
          />
        </div>
        <p className="text-sm font-bold text-center text-primary mb-0.5">{crystal.name}</p>
        {tagline && (
          <p className="text-xs text-center text-white font-medium">{tagline}</p>
        )}
        <p className="text-[11px] text-center text-primary/80 mt-1 font-medium">Tap to learn more →</p>
      </div>
    );
  }

  // Mini variant (used in chakra healing section)
  if (variant === 'mini') {
    return (
      <div 
        className="flex-shrink-0 w-28 cursor-pointer"
        onClick={onClick}
      >
        <div className="aspect-square rounded-xl overflow-hidden bg-background/80 mb-1 flex items-center justify-center p-2">
          <img 
            src={crystal.image} 
            alt={crystal.name}
            className="w-20 h-20 object-contain drop-shadow-sm"
          />
        </div>
        <p className="text-xs text-center text-primary truncate">{crystal.name}</p>
      </div>
    );
  }

  // Inline variant (used in moon ceremonies and vibrations)
  if (variant === 'inline') {
    return (
      <div 
        className="p-4 bg-muted/30 rounded-xl cursor-pointer hover:shadow-md transition-all border-0"
        onClick={onClick}
      >
        <div className="flex items-center gap-4 mb-2">
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-background/80 flex items-center justify-center p-2">
            <img 
              src={crystal.image} 
              alt={crystal.name}
              className="w-12 h-12 object-contain drop-shadow-sm"
            />
          </div>
          <span className="text-sm font-medium text-primary">{crystal.name}</span>
        </div>
        {reason && <p className="text-xs text-primary/80">{reason}</p>}
        {showVibration && crystal.vibrationFrequency && (
          <p className="text-xs text-primary/70 line-clamp-2">{crystal.vibrationFrequency}</p>
        )}
      </div>
    );
  }

  // Detail variant (used in crystal detail view)
  if (variant === 'detail') {
    return (
      <div className="aspect-square max-w-xs mx-auto mb-6 rounded-2xl overflow-visible bg-muted/20 flex items-center justify-center p-4">
        <img 
          src={crystal.image} 
          alt={crystal.name}
          className="w-64 h-64 object-contain drop-shadow-lg scale-110"
        />
      </div>
    );
  }

  return null;
}
